/** Show the code, then ask whether the account that approved it is this person's. */

import { useEffect, useState } from "react";

import {
  Banner,
  Button,
  CopyField,
  DataList,
  EmptyState,
  MetricTile,
  PageHeader,
  Panel,
  Skeleton,
  Stack,
} from "@/components/ui";

import { AgentProblem } from "./AgentProblem";
import { MachineAccountNotice } from "./MachineAccountNotice";
import { AgentError, agentApi } from "./client";
import { clock, orNone, secondsUntil, yesNo } from "./format";
import { useAgentPairing, usePairingCall } from "./queries";

/** A one second tick, running only while something on screen counts down. */
function useTick(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

export function AgentPairing() {
  const pairing = useAgentPairing();
  const begin = usePairingCall(agentApi.begin);
  const reveal = usePairingCall(agentApi.reveal);
  const poll = usePairingCall(agentApi.poll);
  const confirm = usePairingCall(agentApi.confirm);
  const decline = usePairingCall(agentApi.decline);
  const unpair = usePairingCall(agentApi.unpair);

  const screen = pairing.data;
  const now = useTick(screen?.phase === "waiting");

  if (pairing.isPending) {
    return <Skeleton height="14rem" />;
  }
  if (pairing.isError || screen === undefined) {
    return (
      <AgentProblem
        error={pairing.error}
        onRetry={() => void pairing.refetch()}
      />
    );
  }

  const refused = [begin, reveal, poll, confirm, decline, unpair]
    .map((call) => call.error)
    .find((error): error is AgentError => error instanceof AgentError);

  const ask = (
    <Button
      variant="primary"
      onClick={() => begin.mutate()}
      loading={begin.isPending}
    >
      Ask for a code
    </Button>
  );

  return (
    <Stack gap={5}>
      <MachineAccountNotice machineId={screen.machine_id} />

      {refused === undefined ? null : (
        <Banner tone="danger" title={refused.code}>
          {refused.message}
        </Banner>
      )}

      {screen.credential_protected ? null : (
        <Banner tone="danger" title="Credential not protected" />
      )}

      {screen.phase === "awaiting_confirmation" ? (
        <Stack gap={6}>
          <PageHeader
            title={`Is ${orNone(screen.approved_by)} your account?`}
          />
          <Stack direction="row" gap={5}>
            <Button
              size="lg"
              onClick={() => confirm.mutate()}
              loading={confirm.isPending}
            >
              Confirm
            </Button>
            <Button
              size="lg"
              onClick={() => decline.mutate()}
              loading={decline.isPending}
            >
              Decline
            </Button>
          </Stack>
        </Stack>
      ) : null}

      {screen.phase === "waiting" ? (
        <Panel title="Code">
          <Stack gap={5}>
            {screen.code_visible && screen.user_code !== null ? (
              <CopyField value={screen.user_code} label="Code" group large />
            ) : (
              <Button
                onClick={() => reveal.mutate()}
                loading={reveal.isPending}
              >
                Show the code
              </Button>
            )}
            <div aria-live="polite">
              <MetricTile
                value={clock(secondsUntil(screen.expires_at, now))}
                label="Expires in"
              />
            </div>
            <Stack direction="row" gap={4}>
              <Button onClick={() => poll.mutate()} loading={poll.isPending}>
                Check for approval
              </Button>
            </Stack>
          </Stack>
        </Panel>
      ) : null}

      {screen.phase === "paired" ? (
        <Stack gap={5}>
          <Panel title="This machine">
            <DataList
              items={[
                {
                  id: "machine",
                  title: orNone(screen.label),
                  fields: [
                    {
                      label: "Machine id",
                      value: orNone(screen.machine_id),
                      mono: true,
                    },
                    {
                      label: "Fingerprint",
                      value: orNone(screen.fingerprint),
                      mono: true,
                    },
                    {
                      label: "Credential protected",
                      value: yesNo(screen.credential_protected),
                    },
                  ],
                },
              ]}
            />
          </Panel>
          <Stack direction="row" gap={4}>
            <Button
              variant="destructive"
              onClick={() => unpair.mutate()}
              loading={unpair.isPending}
            >
              Unpair
            </Button>
          </Stack>
        </Stack>
      ) : null}

      {screen.phase === "unpaired" ? (
        <EmptyState headline="Not paired" action={ask} />
      ) : null}
      {screen.phase === "expired" ? (
        <EmptyState headline="Code expired" action={ask} />
      ) : null}
      {screen.phase === "declined" ? (
        <EmptyState headline="Declined" action={ask} />
      ) : null}
    </Stack>
  );
}
